import { useEffect, useState } from "react";
import { Database, Download, FileArchive } from "lucide-react";
import { LocalSyncPanel } from "./LocalSyncPanel";
import { formatDate } from "@mavatech/shared";
import { Button, Input, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

export function TaxBackupTab() {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const cfg = trpc.uva.getConfig.useQuery();
  const backupAccess = trpc.backup.access.useQuery();
  const backups = trpc.backup.list.useQuery(undefined, { enabled: !!backupAccess.data?.allowed });

  const [besteuerungsart, setBesteuerungsart] = useState("soll");
  const [ustPflichtigAb, setUstPflichtigAb] = useState("");
  const [uvaPeriode, setUvaPeriode] = useState("monat");

  useEffect(() => {
    if (!cfg.data) return;
    setBesteuerungsart(cfg.data.besteuerungsart);
    setUstPflichtigAb(cfg.data.ustPflichtigAb);
    setUvaPeriode(cfg.data.uvaPeriode);
  }, [cfg.data]);

  const save = trpc.uva.saveConfig.useMutation({
    onSuccess: () => {
      utils.uva.getConfig.invalidate();
      utils.uva.compute.invalidate();
      addToast({ type: "success", title: "Gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const createBackup = trpc.backup.create.useMutation({
    onSuccess: (r) => {
      utils.backup.list.invalidate();
      addToast({
        type: "success",
        title: "Backup erstellt",
        description: `${r.name} · ${(r.sizeBytes / 1024).toFixed(0)} KB`,
      });
    },
    onError: (e) =>
      addToast({
        type: "error",
        title: "Backup fehlgeschlagen",
        description: e.message,
      }),
  });

  return (
    <div className="p-6 max-w-3xl space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Umsatzsteuer
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Besteuerungsart"
            value={besteuerungsart}
            onChange={(e) => setBesteuerungsart(e.target.value)}
            options={[
              { value: "soll", label: "Soll (Rechnungsdatum)" },
              { value: "ist", label: "Ist (Zahlungsdatum)" },
            ]}
          />
          <Select
            label="UVA-Zeitraum"
            value={uvaPeriode}
            onChange={(e) => setUvaPeriode(e.target.value)}
            options={[
              { value: "monat", label: "monatlich" },
              { value: "quartal", label: "quartalsweise" },
            ]}
          />
          <Input
            label="USt-pflichtig ab (optional)"
            type="date"
            value={ustPflichtigAb}
            onChange={(e) => setUstPflichtigAb(e.target.value)}
          />
        </div>
        <div className="mt-4">
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              save.mutate({
                besteuerungsart: besteuerungsart as never,
                ustPflichtigAb,
                uvaPeriode: uvaPeriode as never,
              })
            }
          >
            Speichern
          </Button>
        </div>
        <p className="text-xs text-text-tertiary mt-2">
          Kennzahlen-Zuordnung (U30) mit dem Steuerberater bestätigen. Für eine
          GmbH ist üblicherweise Soll-Besteuerung zutreffend.
        </p>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Dokumente lokal sichern
        </h3>
        <a href="/export/documents">
          <Button variant="primary">
            <FileArchive className="w-4 h-4" /> Alle Angebote &amp; Rechnungen
            als ZIP herunterladen
          </Button>
        </a>
        <p className="text-xs text-text-tertiary mt-2">
          Lädt alle erzeugten Angebots-, Rechnungs-, Vertrags- und Beleg-PDFs
          gebündelt als ZIP-Datei herunter (Ordner je Dokumentart) — landet im
          Download-Ordner deines Browsers.
        </p>

        <div className="mt-4 pt-4 border-t border-border-subtle">
          <h4 className="text-xs font-semibold text-text-primary mb-2">
            Alle Daten exportieren (Ausstiegspaket)
          </h4>
          <a href="/export/all">
            <Button variant="secondary">
              <FileArchive className="w-4 h-4" /> Alle Daten &amp; Dokumente
              als ZIP herunterladen
            </Button>
          </a>
          <p className="text-xs text-text-tertiary mt-2">
            Kunden, Objekte, Angebote, Rechnungen, Aufträge, Verträge, Belege,
            Ausgaben, eigene Artikel und alle PDFs — die Daten als JSON-Dateien
            (mit Excel/Power Query lesbar). Nur für Inhaber/Administratoren.
            Ohne Passwörter und ohne Händler-Katalog.
          </p>
        </div>

        <div className="mt-4 pt-4 border-t border-border-subtle">
          <h4 className="text-xs font-semibold text-text-primary mb-2">
            Automatischer Ordner-Sync (Chrome/Edge)
          </h4>
          <LocalSyncPanel />
        </div>
      </section>

      {backupAccess.data?.allowed && (
      <section>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Datenbank-Sicherung
        </h3>
        <Button
          variant="secondary"
          loading={createBackup.isPending}
          onClick={() => createBackup.mutate()}
        >
          <Database className="w-4 h-4" /> Backup jetzt erstellen
        </Button>
        <p className="text-xs text-text-tertiary mt-2">
          Erzeugt einen vollständigen SQL-Dump im Ordner <code>backups/</code>{" "}
          auf dem Server. Im gehosteten Betrieb zusätzlich zu den Sicherungen
          der Managed-Datenbank.
        </p>
        {!!backups.data?.length && (
          <div className="mt-3 space-y-1.5">
            {backups.data.map((b) => (
              <a
                key={b.name}
                href={`/export/backup/${encodeURIComponent(b.name)}`}
                className="flex items-center justify-between card p-2.5 text-sm hover:border-accent"
              >
                <span className="text-text-secondary flex items-center gap-2">
                  <Download className="w-3.5 h-3.5" /> {b.name}
                </span>
                <span className="text-text-tertiary text-xs">
                  {(b.sizeBytes / 1024).toFixed(0)} KB ·{" "}
                  {formatDate(b.modifiedAt)}
                </span>
              </a>
            ))}
          </div>
        )}
      </section>
      )}
    </div>
  );
}
